var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo, useEffect, useCallback } from 'react';
import { useFormikContext } from 'formik';
import FormItem from './FormItem';
import validate from './validate';
import { toArray, } from '../../utils';
import { cloneElement, isValidElement } from '../../utils/reactNode';
var MemoInput = React.memo(function (_a) {
    var children = _a.children;
    return children;
}, function (prev, next) { return prev.value === next.value && prev.update === next.update; });
/**
 * @description: 输入框验证，显示报错
 * @props {*} FormItemProps
 */
var FormItemIndex = React.forwardRef(function (props, ref) {
    var _a, _b, _c;
    var name = props.name, max = props.max, size = props.size, children = props.children, labelProp = props.label, requiredProp = props.required, rulesProp = props.rules, triggerProp = props.trigger, fastProp = props.fast, onChange = props.onChange, onBlur = props.onBlur, FormItemProps = __rest(props, ["name", "max", "size", "children", "label", "required", "rules", "trigger", "fast", "onChange", "onBlur"]);
    var rules = useMemo(function () {
        var _rules = [];
        // 添加必填规则
        if (requiredProp) {
            _rules.push({
                type: 'required'
            });
        }
        // 添加最大长度限制
        if (max) {
            _rules.push({
                type: 'max'
            });
        }
        return _rules.concat(rulesProp);
    }, [requiredProp, rulesProp, max]);
    var required = useMemo(function () {
        var _a;
        if (requiredProp) {
            return requiredProp;
        }
        var requiredRule = rulesProp === null || rulesProp === void 0 ? void 0 : rulesProp.find(function (rule) {
            if (typeof rule === 'object') {
                return rule.required === true;
            }
        });
        return ((_a = requiredRule) === null || _a === void 0 ? void 0 : _a.required) || false;
    }, [rules]);
    var label = useMemo(function () {
        var _a;
        if (required && (labelProp && typeof labelProp === 'string' && !((_a = labelProp === null || labelProp === void 0 ? void 0 : labelProp.endsWith) === null || _a === void 0 ? void 0 : _a.call(labelProp, '*')))) {
            return labelProp + "*";
        }
        return labelProp;
    }, [labelProp]);
    var _d = ((useFormikContext === null || useFormikContext === void 0 ? void 0 : useFormikContext()) || {}), registerField = _d.registerField, unregisterField = _d.unregisterField, getFieldProps = _d.getFieldProps, getFieldMeta = _d.getFieldMeta, getFieldHelpers = _d.getFieldHelpers, 
    // 是否使用shouldMemoUpdate进行优化
    _e = _d.fast, 
    // 是否使用shouldMemoUpdate进行优化
    fast = _e === void 0 ? fastProp : _e, 
    // 触发验证时机
    trigger = _d.trigger;
    var field = getFieldProps === null || getFieldProps === void 0 ? void 0 : getFieldProps({ name: name });
    var meta = getFieldMeta === null || getFieldMeta === void 0 ? void 0 : getFieldMeta(name);
    var helpers = getFieldHelpers === null || getFieldHelpers === void 0 ? void 0 : getFieldHelpers(name);
    useEffect(function () {
        if (name) {
            registerField === null || registerField === void 0 ? void 0 : registerField(name, {
                // @ts-ignore
                validate: function (value) {
                    var error = validate({
                        value: value,
                        rules: rules,
                        max: max,
                        label: labelProp
                    });
                    return error;
                }
            });
        }
        return function () {
            if (name) {
                unregisterField === null || unregisterField === void 0 ? void 0 : unregisterField(name);
            }
        };
    }, [registerField, unregisterField, name, rules]);
    var error = useMemo(function () {
        if (meta === null || meta === void 0 ? void 0 : meta.touched) {
            return meta === null || meta === void 0 ? void 0 : meta.error;
        }
    }, [meta]);
    var formatEvent = useCallback(function (event, newValue) {
        var _a, _b;
        var value;
        if (newValue !== undefined && !isValidElement(newValue)) {
            value = newValue;
        }
        else if (((_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.value) !== undefined) {
            value = (_b = event === null || event === void 0 ? void 0 : event.target) === null || _b === void 0 ? void 0 : _b.value;
        }
        else {
            value = event;
        }
        return {
            target: {
                name: name,
                value: value,
            },
            type: props.type || 'custom',
        };
    }, [name]);
    var triggers = useMemo(function () {
        // 优先使FromItemProps
        if (triggerProp != null) {
            return toArray(triggerProp);
        }
        // useForm 返回的trigger
        if (trigger != null) {
            return toArray(trigger);
        }
        // 默认支持 onChange时验证和onBlur时验证
        return ['onChange', 'onBlur'];
    }, [triggerProp, trigger]);
    var handleChange = function () {
        var _a;
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (triggers.includes('onChange')) {
            if ((meta === null || meta === void 0 ? void 0 : meta.touched) === false) {
                helpers === null || helpers === void 0 ? void 0 : helpers.setTouched(true);
            }
        }
        (_a = field === null || field === void 0 ? void 0 : field.onChange) === null || _a === void 0 ? void 0 : _a.call(field, formatEvent.apply(void 0, args));
        onChange === null || onChange === void 0 ? void 0 : onChange.apply(void 0, args);
    };
    var handleBlur = function () {
        var _a;
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (triggers.includes('onBlur')) {
            (_a = field === null || field === void 0 ? void 0 : field.onBlur) === null || _a === void 0 ? void 0 : _a.call(field, formatEvent.apply(void 0, args));
        }
        onBlur === null || onBlur === void 0 ? void 0 : onBlur.apply(void 0, args);
    };
    // 触发验证器
    var triggerEvents = {
        onChange: handleChange,
        onBlur: handleBlur,
    };
    // ======================= Children =======================
    var childNode = null;
    var mergedControl = {
        name: name,
        // @ts-ignore
        error: error,
    };
    if (typeof children === 'function') {
        childNode = children(__assign(__assign(__assign({}, mergedControl), triggerEvents), { value: field === null || field === void 0 ? void 0 : field.value, size: size, label: label }));
    }
    else if (isValidElement(children)) {
        // 如果children没有lable 则使用formItem.props.lable
        if (((_a = children === null || children === void 0 ? void 0 : children.props) === null || _a === void 0 ? void 0 : _a.label) === undefined) {
            mergedControl.label = label;
        }
        // 如果children没有value 则使用formItem.props.size
        if (((_b = children === null || children === void 0 ? void 0 : children.props) === null || _b === void 0 ? void 0 : _b.size) === undefined) {
            mergedControl.size = size;
        }
        // 如果children没有value 则使用 field.value
        if (((_c = children === null || children === void 0 ? void 0 : children.props) === null || _c === void 0 ? void 0 : _c.value) === undefined) {
            mergedControl.value = field === null || field === void 0 ? void 0 : field.value;
        }
        var childProps_1 = __assign(__assign({}, children === null || children === void 0 ? void 0 : children.props), mergedControl);
        // events validate
        Object.keys(triggerEvents).forEach(function (eventName) {
            childProps_1[eventName] = function () {
                var _a, _b, _c;
                var arg = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    arg[_i] = arguments[_i];
                }
                (_a = triggerEvents[eventName]) === null || _a === void 0 ? void 0 : _a.call.apply(_a, __spreadArray([triggerEvents], arg, false));
                (_c = (_b = children.props)[eventName]) === null || _c === void 0 ? void 0 : _c.call.apply(_c, __spreadArray([_b], arg, false));
            };
        });
        childNode = (React.createElement(MemoInput, { value: childProps_1 === null || childProps_1 === void 0 ? void 0 : childProps_1.value, update: children }, cloneElement(children, childProps_1)));
    }
    return (React.createElement(FormItem, __assign({ ref: ref, fast: fast, name: name, max: max, size: size, label: label, value: field === null || field === void 0 ? void 0 : field.value, 
        // @ts-ignore
        error: error, onChange: handleChange, onBlur: handleBlur }, FormItemProps), childNode));
});
FormItemIndex.defaultProps = {
    rules: [],
    shouldMemoUpdate: function (prev, next) {
        var _a, _b;
        if (prev.fast === true) {
            return (prev.name === next.name
                && prev.value === next.value
                && prev.label === next.label
                && prev.max === next.max
                && prev.length === next.length
                && prev.extra === next.extra
                && prev.error === next.error
                && (prev.options === next.options && ((_a = prev.options) === null || _a === void 0 ? void 0 : _a.length) === ((_b = next.options) === null || _b === void 0 ? void 0 : _b.length))
                && Object.keys(prev).length === Object.keys(next).length);
        }
        return false;
    }
};
export default FormItemIndex;
